import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PremiumLayout from '../components/layout/PremiumLayout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import '../styles/PremiumDashboard.css';

/**
 * Dashboard Premium OP - Design cu grafice și statistici (GA4).
 * Folosește PremiumLayout pentru Sidebar + Header + Dark/Light mode.
 */
export default function PremiumDashboardOP() {
    const { user } = useAuth();
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);
    const [timeframe, setTimeframe] = useState(30);
    const [selectedSubordinate, setSelectedSubordinate] = useState('all');
    const [subordinates, setSubordinates] = useState({ type: 'agenti', items: [] });

    const [platformStats, setPlatformStats] = useState({
        agenti: 0,
        leads: 0,
        landingPages: 0,
        leadsAstazi: 0,
        structura: { retele: 0, regiuni: 0, manageri: 0, agenti: 0 },
    });

    const [ambassadorStats, setAmbassadorStats] = useState({ totalAmbassadors: 0, totalReferred: 0 });

    const [ga4Dashboard, setGa4Dashboard] = useState({
        overview: { sessions: 0, users: 0, newUsers: 0, bounceRate: 0 },
        timeline: { labels: [], sessions: [], users: [] },
        trafficSources: [],
        topPages: [],
        countries: [],
        devices: [],
    });

    const [realtimeData, setRealtimeData] = useState({ activeUsers: 0, activePages: [] });

    useEffect(() => {
        const fetchSubordinates = async () => {
            try {
                const res = await api.get('/op/subordinates');
                if (res.data.success && res.data.items) {
                    setSubordinates({ type: res.data.type || 'agenti', items: res.data.items });
                }
            } catch (err) {
                console.error('Eroare încărcare subalterni:', err);
            }
        };
        fetchSubordinates();
    }, []);

    useEffect(() => {
        const fetchPlatformStats = async () => {
            try {
                const q = selectedSubordinate && selectedSubordinate !== 'all' ? `?subordinateId=${selectedSubordinate}` : '';
                const res = await api.get(`/op/stats${q}`);
                if (res.data.success && res.data.stats) {
                    setPlatformStats((prev) => ({ ...prev, ...res.data.stats, structura: res.data.stats.structura || prev.structura }));
                }
            } catch (err) {
                console.error('Eroare încărcare statistici platformă:', err);
            }
        };
        fetchPlatformStats();
    }, [selectedSubordinate]);

    useEffect(() => {
        const fetchAmbassadorStats = async () => {
            try {
                const params = selectedSubordinate && selectedSubordinate !== 'all' ? { subordinateId: selectedSubordinate } : {};
                const res = await api.get('/ambassadors/stats', { params });
                if (res.data.success && res.data.stats) {
                    setAmbassadorStats(res.data.stats);
                }
            } catch (err) {
                console.error('Eroare încărcare statistici ambasadori:', err);
            }
        };
        fetchAmbassadorStats();
    }, [selectedSubordinate]);

    useEffect(() => {
        const fetchGa4Dashboard = async () => {
            try {
                const subParam = selectedSubordinate && selectedSubordinate !== 'all' ? `&subordinateId=${selectedSubordinate}` : '';
                const res = await api.get(`/op/analytics/dashboard?days=${timeframe}${subParam}`);
                if (res.data.success) {
                    setGa4Dashboard({
                        overview: res.data.overview || { sessions: 0, users: 0, newUsers: 0, bounceRate: 0 },
                        timeline: res.data.timeline || { labels: [], sessions: [], users: [] },
                        trafficSources: res.data.trafficSources || [],
                        topPages: res.data.topPages || [],
                        countries: res.data.countries || [],
                        devices: res.data.devices || [],
                    });
                }
            } catch (err) {
                console.error('Eroare încărcare GA4 dashboard:', err);
            }
        };
        fetchGa4Dashboard();
    }, [timeframe, selectedSubordinate]);

    const fetchRealtime = async () => {
        try {
            const q = selectedSubordinate && selectedSubordinate !== 'all' ? `?subordinateId=${selectedSubordinate}` : '';
            const res = await api.get(`/op/analytics/realtime${q}`);
            if (res.data.success) {
                setRealtimeData({
                    activeUsers: res.data.activeUsers ?? 0,
                    activePages: res.data.activePages ?? [],
                });
            }
        } catch (err) {
            console.error('Eroare încărcare Realtime:', err);
        }
    };

    useEffect(() => {
        fetchRealtime();
        const interval = setInterval(fetchRealtime, 60000);
        return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchRealtime intentionally omitted to avoid interval reset every render
    }, [selectedSubordinate]);

    const channelEmoji = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('organic')) return '🔍';
        if (n.includes('direct')) return '🔗';
        if (n.includes('social')) return '📱';
        if (n.includes('email')) return '📧';
        if (n.includes('referral')) return '📌';
        if (n.includes('paid') || n.includes('cpc')) return '💰';
        return '📊';
    };
    const deviceEmoji = (name) => {
        const n = (name || '').toLowerCase();
        if (n.includes('mobile')) return '📱';
        if (n.includes('desktop')) return '🖥️';
        if (n.includes('tablet')) return '💻';
        return '📱';
    };
    const countryFlag = (countryId) => {
        if (!countryId || countryId.length !== 2) return '🌍';
        return countryId.toUpperCase().split('').map((c) =>
            String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))
        ).join('');
    };

    const barGradients = [
        'linear-gradient(90deg,#4f6ef7,#7c3aed)',
        'linear-gradient(90deg,#10b981,#059669)',
        'linear-gradient(90deg,#f59e0b,#d97706)',
        'linear-gradient(90deg,#ec4899,#db2777)',
        'linear-gradient(90deg,#f97316,#ea580c)',
    ];

    useEffect(() => {
        if (!chartRef.current) return;

        const { labels = [], sessions: sesData = [], users: usrData = [] } = ga4Dashboard.timeline;

        import('chart.js/auto').then(({ default: Chart }) => {
            if (chartInstanceRef.current) chartInstanceRef.current.destroy();
            chartInstanceRef.current = new Chart(chartRef.current, {
                type: 'line',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Sesiuni',
                            data: sesData,
                            borderColor: 'rgb(79,110,247)',
                            backgroundColor: 'rgba(79,110,247,0.06)',
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                        },
                        {
                            label: 'Utilizatori',
                            data: usrData,
                            borderColor: 'rgb(52,211,153)',
                            backgroundColor: 'rgba(52,211,153,0.04)',
                            borderWidth: 2,
                            fill: true,
                            tension: 0.4,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            display: true,
                            position: 'top',
                            labels: {
                                color: '#6b7280',
                                font: { size: 11, family: 'DM Sans' },
                                boxWidth: 10,
                                padding: 16,
                            },
                        },
                        tooltip: {
                            backgroundColor: '#161a28',
                            borderColor: 'rgba(255,255,255,0.1)',
                            borderWidth: 1,
                            titleColor: '#f0f2ff',
                            bodyColor: '#9ca3c8',
                            padding: 10,
                        },
                    },
                    scales: {
                        x: {
                            ticks: { color: '#6b7280', font: { size: 10 }, maxTicksLimit: 8 },
                            grid: { color: 'rgba(255,255,255,0.03)' },
                            border: { display: false },
                        },
                        y: {
                            ticks: { color: '#6b7280', font: { size: 10 } },
                            grid: { color: 'rgba(255,255,255,0.04)' },
                            border: { display: false },
                        },
                    },
                },
            });
        });

        return () => {
            if (chartInstanceRef.current) chartInstanceRef.current.destroy();
        };
    }, [ga4Dashboard.timeline]);

    const dropdownLabelAll = (() => {
        const r = (user?.role || 'op').toLowerCase();
        if (r === 'cso') return 'Toate Rețelele';
        if (r === 'retea') return 'Toate Regiunile';
        if (r === 'regiune') return 'Toți Managerii';
        return 'Toată Echipa';
    })();

    const topActions = (
        <>
            <select
                className="agent-filter-select"
                value={selectedSubordinate}
                onChange={(e) => setSelectedSubordinate(e.target.value)}
                title="Filtrează statistici și GA4 pe subaltern"
            >
                <option value="all">{dropdownLabelAll}</option>
                {(subordinates.items || []).map((item) => (
                    <option key={item.id} value={item.id}>
                        {item.numeComplet || item.email || `#${item.id}`}
                    </option>
                ))}
            </select>
            <div className="range-selector">
                <button
                    type="button"
                    className={`range-btn ${timeframe === 7 ? 'active' : ''}`}
                    onClick={() => setTimeframe(7)}
                >
                    7Z
                </button>
                <button
                    type="button"
                    className={`range-btn ${timeframe === 30 ? 'active' : ''}`}
                    onClick={() => setTimeframe(30)}
                >
                    30Z
                </button>
                <button
                    type="button"
                    className={`range-btn ${timeframe === 90 ? 'active' : ''}`}
                    onClick={() => setTimeframe(90)}
                >
                    90Z
                </button>
            </div>
            <div className="ga-badge">
                <span className="ga-dot" />
                GA4 Conectat
            </div>
        </>
    );

    return (
        <PremiumLayout
            title="Dashboard Overview"
            subtitle="Platformă + Google Analytics"
            topActions={topActions}
        >
            {/* PLATFORM KPIs */}
            <div className="section-header">
                <div className="section-title">Platformă</div>
                <span className="section-badge badge-platform">Lead Platform</span>
                <div className="section-line" />
            </div>

            <div className="kpi-grid" style={{ marginBottom: 0 }}>
                <div className="kpi-card" style={{ animationDelay: '0.05s', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Structura Mea</div>
                        <div className="kpi-icon-wrap icon-blue">🏢</div>
                    </div>
                    <div className="kpi-value c-blue" style={{ fontSize: '0.95rem', lineHeight: 1.3 }}>
                        {(() => {
                            const s = platformStats.structura || {};
                            const retele = Number(s.retele) || 0;
                            const regiuni = Number(s.regiuni) || 0;
                            const manageri = Number(s.manageri) || 0;
                            const agenti = Number(s.agenti) || 0;
                            const parts = [];
                            if (retele > 0) parts.push(`${retele} Rețele`);
                            if (regiuni > 0) parts.push(`${regiuni} Regiuni`);
                            if (manageri > 0) parts.push(`${manageri} Manageri`);
                            if (agenti > 0) parts.push(`${agenti} Agenți`);
                            if (parts.length > 0) return parts.join(' · ');
                            if (platformStats.structura && typeof platformStats.structura === 'object') return 'Niciun subaltern';
                            return '—';
                        })()}
                    </div>
                    <div className="kpi-trend" style={{ marginTop: '8px' }}>
                        <Link
                            to="/op/structura"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-500/15 text-blue-600 dark:text-blue-400 hover:bg-blue-500/25 border border-blue-500/30 transition-colors"
                        >
                            {user?.role === 'cso' && 'Gestionează Rețele'}
                            {user?.role === 'retea' && 'Gestionează Regiuni'}
                            {user?.role === 'regiune' && 'Gestionează Manageri'}
                            {(user?.role === 'manager' || user?.role === 'op') && 'Gestionează Agenți'}
                        </Link>
                    </div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.1s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Total Lead-uri</div>
                        <div className="kpi-icon-wrap icon-green">📥</div>
                    </div>
                    <div className="kpi-value c-green">{(platformStats.leads ?? 0).toLocaleString('ro-RO')}</div>
                    <div className="kpi-trend trend-up">↑ +12% <span className="trend-muted">vs luna trecută</span></div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.15s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Landing Pages</div>
                        <div className="kpi-icon-wrap icon-amber">🌐</div>
                    </div>
                    <div className="kpi-value c-amber">{platformStats.landingPages ?? 0}</div>
                    <div className="kpi-trend trend-neutral">Active acum</div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.2s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Lead-uri Astăzi</div>
                        <div className="kpi-icon-wrap icon-purple">⚡</div>
                    </div>
                    <div className="kpi-value c-purple">{platformStats.leadsAstazi ?? 0}</div>
                    <div className="kpi-trend trend-up">↑ +8 <span className="trend-muted">față de ieri</span></div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.22s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Total Ambasadori</div>
                        <div className="kpi-icon-wrap icon-teal">🌟</div>
                    </div>
                    <div className="kpi-value c-teal">{ambassadorStats.totalAmbassadors ?? 0}</div>
                    <div className="kpi-trend trend-neutral">Au adus {ambassadorStats.totalReferred ?? 0} clienți noi</div>
                </div>
            </div>

            {/* GA KPIs */}
            <div className="section-header">
                <div className="section-title">Google Analytics</div>
                <span className="section-badge badge-ga">roasigur.ro · Ultimele {timeframe} zile</span>
                <div className="section-line" />
            </div>

            <div className="kpi-grid-3" style={{ marginBottom: 0 }}>
                <div className="kpi-card" style={{ animationDelay: '0.25s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Sesiuni</div>
                        <div className="kpi-icon-wrap icon-teal">📊</div>
                    </div>
                    <div className="kpi-value c-teal">{(ga4Dashboard.overview?.sessions ?? 0).toLocaleString('ro-RO')}</div>
                    <div className="kpi-trend trend-up">↑ +18.4% <span className="trend-muted">vs anterior</span></div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.3s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Utilizatori</div>
                        <div className="kpi-icon-wrap icon-blue">👤</div>
                    </div>
                    <div className="kpi-value c-blue">{(ga4Dashboard.overview?.users ?? 0).toLocaleString('ro-RO')}</div>
                    <div className="kpi-trend trend-up">↑ +14.2% <span className="trend-muted">vs anterior</span></div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.35s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Utilizatori Noi</div>
                        <div className="kpi-icon-wrap icon-green">✨</div>
                    </div>
                    <div className="kpi-value c-green">{(ga4Dashboard.overview?.newUsers ?? 0).toLocaleString('ro-RO')}</div>
                    <div className="kpi-trend trend-up">↑ +22.1% <span className="trend-muted">vs anterior</span></div>
                </div>
                <div className="kpi-card" style={{ animationDelay: '0.4s' }}>
                    <div className="kpi-top">
                        <div className="kpi-label">Rată Respingere</div>
                        <div className="kpi-icon-wrap icon-amber">↩️</div>
                    </div>
                    <div className="kpi-value c-amber">{ga4Dashboard.overview?.bounceRate ?? 0}%</div>
                    <div className="kpi-trend trend-up">↓ -4.1% <span className="trend-muted">îmbunătățire</span></div>
                </div>
            </div>

            {/* MAIN CHARTS ROW */}
            <div id="grafic-evolutie" className="charts-row" style={{ marginBottom: '16px' }}>
                {/* Sessions Over Time */}
                <div className="card" style={{ animationDelay: '0.45s' }}>
                    <div className="card-header">
                        <div>
                            <div className="card-title">Sesiuni &amp; Utilizatori în Timp</div>
                            <div className="card-sub">Evoluție zilnică · ultimele {timeframe} zile</div>
                        </div>
                        <span className="card-tag tag-ga">GA4</span>
                    </div>
                    <div className="chart-wrap" style={{ height: '150px' }}>
                        <canvas ref={chartRef} />
                    </div>
                </div>

                {/* Realtime */}
                <div
                    id="real-time"
                    className="card border-emerald-500/20 dark:border-emerald-400/25"
                    style={{ animationDelay: '0.5s' }}
                >
                    <div className="card-header">
                        <div>
                            <div className="card-title text-emerald-600 dark:text-emerald-400">⚡ Real-Time</div>
                            <div className="card-sub">Utilizatori activi acum</div>
                        </div>
                        <span className="card-tag tag-ga">Live</span>
                    </div>
                    <div className="realtime-number text-emerald-600 dark:text-emerald-400">
                        {realtimeData.activeUsers}
                    </div>
                    <div className="realtime-label text-gray-500 dark:text-gray-400">
                        {realtimeData.activeUsers === 0
                            ? 'Așteptăm vizitatori...'
                            : `${realtimeData.activeUsers} utilizator${realtimeData.activeUsers === 1 ? '' : 'i'} activ${realtimeData.activeUsers === 1 ? '' : 'i'} în acest moment`}
                    </div>
                    {(realtimeData.activePages || []).length > 0 &&
                        realtimeData.activePages.map((p, i) => (
                            <div key={`${(p.path || '').replace(/\s/g, '_')}-${p.activeUsers ?? 0}-${i}`} className="active-page">
                                <div className="active-dot" />
                                <div className="active-url text-gray-600 dark:text-gray-300">
                                    {p.path || '/'}
                                </div>
                                <div className="active-count text-emerald-600 dark:text-emerald-400">
                                    {p.activeUsers ?? 0}
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* BOTTOM CHARTS */}
            <div className="charts-row-2">
                {/* Surse trafic */}
                <div id="surse-trafic" className="card" style={{ animationDelay: '0.55s' }}>
                    <div className="card-header">
                        <div>
                            <div className="card-title">Surse de Trafic</div>
                            <div className="card-sub">De unde vin vizitatorii</div>
                        </div>
                        <span className="card-tag tag-ga">GA4</span>
                    </div>
                    {(ga4Dashboard.trafficSources || []).map((src, i) => (
                        <div key={src.name + i} className="source-row">
                            <div className="source-name">{channelEmoji(src.name)} {src.name}</div>
                            <div className="source-bar-bg">
                                <div className="source-bar-fill" style={{ width: `${src.percent || 0}%`, background: barGradients[i % barGradients.length] }} />
                            </div>
                            <div className="source-val">{src.sessions?.toLocaleString('ro-RO') ?? 0}</div>
                            <div className="source-pct">{src.percent ?? 0}%</div>
                        </div>
                    ))}
                </div>

                {/* Top Pages - Top 3 (total din tot array-ul, procent corect) */}
                <div className="card card-top-pages" style={{ animationDelay: '0.6s' }}>
                    <div className="card-header">
                        <div>
                            <div className="card-title">Pagini Top</div>
                            <div className="card-sub">Cele mai vizitate pagini</div>
                        </div>
                        <span className="card-tag tag-ga">GA4</span>
                    </div>
                    <div className="page-rows-wrap" style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
                        {(() => {
                            const topPagesData = ga4Dashboard.topPages || [];
                            const globalTotalViews = ga4Dashboard?.overview?.pageViews ?? ga4Dashboard?.overview?.sessions ?? 1;
                            return topPagesData.slice(0, 3).map((page, index) => {
                                const views = Number(page.views ?? page.screenPageViews ?? 0);
                                const percentage = Math.round((views / globalTotalViews) * 100);
                                const pagePath = page.path ?? page.pagePath ?? 'Necunoscut';
                                return (
                                    <div key={(pagePath || '') + index} className="page-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div className="page-url" style={{ color: 'var(--text-soft)', fontSize: '13px', maxWidth: '60%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {pagePath}
                                        </div>
                                        <div className="page-stats" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span className="page-views" style={{ fontWeight: 700, fontSize: '14px' }}>{views.toLocaleString('ro-RO')}</span>
                                            <span className="page-pct" style={{ fontSize: '12px', width: '32px', textAlign: 'right', color: 'var(--text-muted)' }}>{percentage}%</span>
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </div>

                {/* Geo + Devices */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div id="top-tari" className="card" style={{ animationDelay: '0.65s', flex: 1 }}>
                        <div className="card-header">
                            <div>
                                <div className="card-title">Top Țări</div>
                                <div className="card-sub">Localizare vizitatori</div>
                            </div>
                            <span className="card-tag tag-ga">GA4</span>
                        </div>
                        {(ga4Dashboard.countries || []).map((c, i) => (
                            <div key={(c.name || '') + i} className="geo-row">
                                <div className="geo-flag">{countryFlag(c.countryId)}</div>
                                <div className="geo-name">{c.name}</div>
                                <div className="geo-bar-bg">
                                    <div className="geo-bar-fill" style={{ width: `${c.barPercent ?? 0}%` }} />
                                </div>
                                <div className="geo-val">{(c.sessions ?? 0).toLocaleString('ro-RO')}</div>
                            </div>
                        ))}
                    </div>

                    <div id="dispozitive" className="card" style={{ animationDelay: '0.7s', flex: 1 }}>
                        <div className="card-header">
                            <div>
                                <div className="card-title">Dispozitive</div>
                            </div>
                            <span className="card-tag tag-ga">GA4</span>
                        </div>
                        {(ga4Dashboard.devices || []).map((d, i) => (
                            <div key={(d.name || '') + i} className="device-item">
                                <div className={`device-icon icon-${['blue', 'green', 'amber'][i % 3]}`}>{deviceEmoji(d.name)}</div>
                                <div className="device-name">{d.name}</div>
                                <div className={`device-pct c-${['blue', 'green', 'amber'][i % 3]}`}>{d.percent ?? 0}%</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </PremiumLayout>
    );
}
